// Pick how the background behind you looks: none, blur, a ready-made picture, a company one or your own upload.
// `dark` = inside the call's camera menu; light = the white pre-join lobby. Guests can use a picture for this call only.
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Ban, Droplets, ImagePlus, Loader2, X } from 'lucide-react';
import { listBackgrounds, uploadBackground, deleteBackground, backgroundUrl } from '@/api/morningMeeting';
import { PRESET_GROUPS, presetSrc } from './backgrounds';

const MAX = 15 * 1024 * 1024;
const ACCEPT = 'image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif';
const errMsg = (e, f) => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : f);

function Tile({ tid, label, img, Icon, selected, busy, disabled, dark, onClick, onDelete }) {
  const ring = selected
    ? `ring-2 ring-[#4575A8] ring-offset-2 ${dark ? 'ring-offset-[#0D1B2A]' : 'ring-offset-white'}`
    : dark ? 'ring-1 ring-white/15 hover:ring-white/50' : 'ring-1 ring-[#DDE3EA] hover:ring-[#4575A8]/70';
  return (
    <div className="group relative">
      <button type="button" onClick={onClick} disabled={disabled || busy} title={label} aria-label={label} aria-pressed={!!selected} data-testid={tid}
        className={`relative flex aspect-video w-full flex-col items-center justify-center gap-0.5 overflow-hidden rounded-md transition-shadow duration-150 disabled:cursor-not-allowed disabled:opacity-40 ${ring} ${img ? '' : dark ? 'bg-white/5 text-slate-200' : 'bg-[#F2F5F8] text-[#5C6370]'}`}>
        {img ? <img src={img} alt="" loading="lazy" draggable={false} className="h-full w-full object-cover" /> : (
          <><Icon className="h-4 w-4" /><span className="text-[10px] font-medium leading-none">{label}</span></>
        )}
        {busy && <span className="absolute inset-0 flex items-center justify-center bg-black/45"><Loader2 className="h-4 w-4 animate-spin text-white" /></span>}
      </button>
      {onDelete && (
        <button type="button" onClick={onDelete} aria-label={`Remove ${label}`} title="Remove" data-testid={`${tid}-delete`}
          className="absolute -right-1.5 -top-1.5 hidden h-5 w-5 items-center justify-center rounded-full bg-[#0D1B2A] text-white shadow ring-1 ring-white/30 transition-colors duration-150 hover:bg-red-600 group-hover:flex group-focus-within:flex">
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

function Group({ label, dark, children }) {
  return (
    <div>
      <p className={`mb-1.5 text-[10px] font-bold uppercase tracking-wider ${dark ? 'text-slate-400' : 'text-[#8A94A3]'}`}>{label}</p>
      <div className="grid grid-cols-4 gap-2">{children}</div>
    </div>
  );
}

export default function BackgroundPicker({ dc, guest, dark, tid }) {
  const [lib, setLib] = useState({ company: [], mine: [], can_manage: false });
  const [uploading, setUploading] = useState(null);
  const fileRef = useRef(null);
  const scope = useRef('mine');
  useEffect(() => {
    if (guest) return undefined;
    let off = false;
    listBackgrounds().then((d) => { if (!off && d) setLib(d); }).catch(() => {});
    return () => { off = true; };
  }, [guest]);

  const cur = dc.effect || { type: 'none' };
  const isOn = (type, id) => cur.type === type && (type !== 'image' || cur.id === id);
  const off = !dc.blurSupported;
  const pick = (eff) => dc.setEffect(eff);
  const choose = (s) => { scope.current = s; fileRef.current?.click(); };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX) { toast.error('That picture is too big (15 MB max).'); return; }
    if (guest) {
      pick({ type: 'image', id: 'local', bytes: await file.arrayBuffer(), thumb: URL.createObjectURL(file) });
      return;
    }
    setUploading(scope.current);
    try {
      const bg = await uploadBackground(file, scope.current);
      setLib((l) => ({ ...l, [bg.scope]: [...l[bg.scope], bg] }));
      pick({ type: 'image', id: bg.id });
    } catch (err) { toast.error(errMsg(err, "Couldn't upload that picture.")); }
    setUploading(null);
  };
  const remove = async (bg) => {
    try {
      await deleteBackground(bg.id);
      setLib((l) => ({ ...l, [bg.scope]: l[bg.scope].filter((x) => x.id !== bg.id) }));
      if (isOn('image', bg.id)) pick({ type: 'none' });
    } catch (err) { toast.error(errMsg(err, "Couldn't remove that picture.")); }
  };
  const saved = (bg) => (
    <Tile key={bg.id} tid={`${tid}-saved-${bg.id}`} label={bg.name} img={backgroundUrl(bg.id, true)} dark={dark} disabled={off}
      selected={isOn('image', bg.id)} busy={dc.effectBusy === bg.id} onClick={() => pick({ type: 'image', id: bg.id })}
      onDelete={bg.can_delete ? () => remove(bg) : null} />
  );

  return (
    <div className="space-y-3" data-testid={tid}>
      <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" onChange={onFile} data-testid={`${tid}-file`} />
      {off && <p className={`text-xs ${dark ? 'text-slate-400' : 'text-[#5C6370]'}`}>Backgrounds aren&apos;t available in this browser.</p>}
      <div className="grid grid-cols-4 gap-2">
        <Tile tid={`${tid}-none`} label="None" Icon={Ban} dark={dark} disabled={off} selected={isOn('none')} onClick={() => pick({ type: 'none' })} />
        <Tile tid={`${tid}-blur`} label="Blur" Icon={Droplets} dark={dark} disabled={off} selected={isOn('blur')} busy={dc.effectBusy === 'blur'} onClick={() => pick({ type: 'blur' })} />
        {guest && dc.localBg && (
          <Tile tid={`${tid}-local`} label="Your picture" img={dc.localBg.thumb} dark={dark} disabled={off} selected={isOn('image', 'local')} busy={dc.effectBusy === 'local'} onClick={() => pick(dc.localBg)} />
        )}
        <Tile tid={`${tid}-upload`} label={guest ? 'Your own' : 'Upload'} Icon={ImagePlus} dark={dark} disabled={off} busy={uploading === 'mine'} onClick={() => choose('mine')} />
      </div>
      {PRESET_GROUPS.map((g) => (
        <Group key={g.label} label={g.label} dark={dark}>
          {g.items.map((p) => (
            <Tile key={p.id} tid={`${tid}-${p.id.slice(7)}`} label={p.name} img={presetSrc(p.id, true)} dark={dark} disabled={off}
              selected={isOn('image', p.id)} busy={dc.effectBusy === p.id} onClick={() => pick({ type: 'image', id: p.id })} />
          ))}
        </Group>
      ))}
      {(lib.company.length > 0 || lib.can_manage) && (
        <Group label="Company" dark={dark}>
          {lib.company.map(saved)}
          {lib.can_manage && <Tile tid={`${tid}-upload-company`} label="Add for all" Icon={ImagePlus} dark={dark} disabled={off} busy={uploading === 'company'} onClick={() => choose('company')} />}
        </Group>
      )}
      {lib.mine.length > 0 && <Group label="Yours" dark={dark}>{lib.mine.map(saved)}</Group>}
      {guest && <p className={`text-[11px] ${dark ? 'text-slate-400' : 'text-[#8A94A3]'}`}>Your own picture is used for this call only.</p>}
    </div>
  );
}
